import React, { FC, useState, ChangeEvent, MouseEvent, useContext, useCallback, useMemo } from 'react'
import { Grid, Table, TableBody, TableContainer, TableFooter, TableHead, TableRow } from '@mui/material'
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions'
import { FormattedMessage } from 'react-intl'
import { StyledTableCell, StyledTablePagination, StyledTableRow } from './styledComponents'
import { capitalizeFirstLetter } from '../utils/rechercheContactUtils'
import { ROWS_PER_PAGE_OPTIONS, TABLE_CONTACT_COLUMNS } from '../utils/rechercheContactConstants'
import { RechercheContactContext } from './RechercheContactContext'
import Loader from '../../../components/loader/Loader'
import { renderTableCell } from '../utils/tableUtils'
import { SelectionPaneContext } from '../../../components/selectionPane/components/SelectionPaneContext'
import { makeStyles } from 'tss-react/mui'
import styles from '../../../styles'
import { Client } from '../../../types/localStorageModels'
import { typeContactEnum, typeSocieteEnum } from '../../../enums/typeContactEnum'
import { getKeyByValue } from '../../../utils/ieCompatibility'
import { prospectTypeEnum } from '../../../enums/prospectTypeEnum'
import { useNavigate } from 'react-router'
import links from '../../../enums/linksEnum'
import { getOffreFromDemande } from '../api/rechercheContactApi'
import { isEmpty } from 'lodash'

const ContactsTable: FC = () => {
	const { classes } = useStyles()
	const navigate = useNavigate()

	const { rechercheLoading, resultatsContact, page, setPage } = useContext(RechercheContactContext)
	const { updateCurrentClient, hasCurrentOffer, updateCurrentOffer } = useContext(SelectionPaneContext)

	const [rechercheOffreLoading, setRechercheOffreLoading] = useState<boolean>(false)
	const [selectedRow, setSelectedRow] = useState<number>()
	const [rowsPerPage, setRowsPerPage] = useState<number>(10)

	const rowsToDisplay = useMemo(() => rowsPerPage > 0 ? resultatsContact.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : resultatsContact, [rowsPerPage, resultatsContact, page])

	const handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage)

	const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10))
		setPage(0)
	}

	const onClickRow = useCallback((client: Client) => {
		if (client.type === typeSocieteEnum.SOCIETE_SUSPECT_RESIDENTIEL || client.type === typeContactEnum.SUSPECT_RESIDENTIEL) {
			updateCurrentClient(client)
			// Si pas d'offre courante, on va checker s'il existe un lien demande-offre
			if (!hasCurrentOffer) {
				setRechercheOffreLoading(true)
				getOffreFromDemande(client.id)
					.then((offre) => {
						if (!isEmpty(offre)) {
							updateCurrentOffer(offre)
							navigate(links.PLANIFICATION_VISITE)
						} else {
							navigate(links.RECHERCHE_OFFRE)
						}
					})
					.finally(() => setRechercheOffreLoading(false))
			} else {
				navigate(links.PLANIFICATION_VISITE)
			}
		} else {
			navigate(`${links.DETAILS_CONTACT}/${client.id}`, {
				state: {
					client_id: client.id,
					prospect_type: client.prospect_type,
					type: client.type,
					prenom: client.prenom,
					nom: client.nom
				}
			})
		}
	}, [hasCurrentOffer, navigate, updateCurrentOffer, updateCurrentClient])

	return (
		<TableContainer>
			<Table sx={{ minWidth: 650 }}>
				<TableHead>
					<TableRow>
						{
							TABLE_CONTACT_COLUMNS.map((column, index) => (
								<StyledTableCell key={`column-${index}`}>
									<FormattedMessage
										id={`rechercheContact.resultats.table.${column}`}
										defaultMessage={capitalizeFirstLetter(column)}
										description="Nom de la colonne"
									/>
								</StyledTableCell>
							))
						}
					</TableRow>
				</TableHead>
				<TableBody>
					{
						rechercheLoading &&
						<StyledTableRow>
							<StyledTableCell colSpan={TABLE_CONTACT_COLUMNS.length}>
								<Loader />
							</StyledTableCell>
						</StyledTableRow>
					}
					{
						!rechercheLoading && (
							resultatsContact.length ? (
								rowsToDisplay.map((client, index) => (
									<StyledTableRow
										key={`row-${index}`}
										onClick={() => {
											if (!rechercheOffreLoading && !rechercheLoading) {
												setSelectedRow(index)
												onClickRow(client)
											}
										}}
										className={classes.tableRow}
									>
										<StyledTableCell>
											<Grid container item alignItems="center">
												{selectedRow === index && rechercheOffreLoading && <Grid item className={classes.loaderRow}><Loader size={15} /></Grid>}
												{client.nom}
											</Grid>
										</StyledTableCell>
										<StyledTableCell>{client.prenom}</StyledTableCell>
										<StyledTableCell>{client.email}</StyledTableCell>
										<StyledTableCell>{client.telephone}</StyledTableCell>
										<StyledTableCell>
											{renderTableCell('contact.type', getKeyByValue(client.prospect_type === prospectTypeEnum.CONTACT ? typeContactEnum : typeSocieteEnum, client.type))}
										</StyledTableCell>
										<StyledTableCell>
											{renderTableCell('contact.origine', client.origine)}
										</StyledTableCell>
									</StyledTableRow>
								))
							) : (
								<StyledTableRow>
									<StyledTableCell colSpan={TABLE_CONTACT_COLUMNS.length}>
										<FormattedMessage
											id="rechercheContact.resultats.table.empty"
											defaultMessage="Aucun contact ne correspond à votre recherche."
											description="Aucun contact ne correspond à votre recherche."
										/>
									</StyledTableCell>
								</StyledTableRow>
							)
						)
					}
				</TableBody>
				<TableFooter>
					<TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
						<StyledTablePagination
							rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
							count={resultatsContact.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							ActionsComponent={TablePaginationActions}
							SelectProps={{ native: true }}
						/>
					</TableRow>
				</TableFooter>
			</Table>
		</TableContainer>
	)
}

const useStyles = makeStyles()(theme => ({
	...styles(theme),
	tableRow: {
		cursor: 'pointer'
	},
	loaderRow: {
		marginRight: 10
	}
}))

export default ContactsTable